import api from '@/shared/utils/apiConfig';
import type { TreatmentCatalogItem } from '../types/treatmentCatalog.types';
import type { ImportCsvPreviewResult, ImportInspectResult } from '@/shared/import-export';
import {
  downloadBlob,
  filenameFromContentDisposition,
} from '@/shared/import-export';

interface TreatmentCatalogParams {
  [key: string]: unknown;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  count?: number;
}

export const treatmentCatalogAPI = {
  getAll: async (params: TreatmentCatalogParams = {}): Promise<ApiResponse<TreatmentCatalogItem[]>> => {
    const response = await api.get('/treatment-catalog', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<TreatmentCatalogItem>> => {
    const response = await api.get(`/treatment-catalog/${id}`);
    return response.data;
  },

  create: async (data: Partial<TreatmentCatalogItem>): Promise<ApiResponse<TreatmentCatalogItem>> => {
    const response = await api.post('/treatment-catalog', data);
    return response.data;
  },

  update: async (id: string, data: Partial<TreatmentCatalogItem>): Promise<ApiResponse<TreatmentCatalogItem>> => {
    const response = await api.put(`/treatment-catalog/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<TreatmentCatalogItem>> => {
    const response = await api.delete(`/treatment-catalog/${id}`);
    return response.data;
  },

  exportCsv: async (options?: { includeInactive?: boolean }): Promise<void> => {
    const response = await api.get('/treatment-catalog/export', {
      params: {
        ...(options?.includeInactive ? { includeInactive: 'true' } : {}),
      },
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'treatment-catalog.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  downloadImportTemplate: async (): Promise<void> => {
    const response = await api.get('/treatment-catalog/import/template', {
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'treatment-catalog-import-template.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  inspectImport: async (
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<ApiResponse<ImportInspectResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/treatment-catalog/import/inspect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },

  importCsv: async (
    file: File,
    dryRun: boolean,
    onProgress?: (percent: number) => void,
    columnMapping?: Record<string, string>,
    skipInvalidRows?: boolean
  ): Promise<ApiResponse<ImportCsvPreviewResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping && Object.keys(columnMapping).length > 0) {
      formData.append('columnMapping', JSON.stringify(columnMapping));
    }
    const response = await api.post('/treatment-catalog/import', formData, {
      params: {
        dryRun: dryRun ? 'true' : 'false',
        ...(skipInvalidRows ? { skipInvalidRows: 'true' } : {}),
      },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },
};

/** @deprecated Use treatmentCatalogAPI instead */
export const treatmentsAPI = treatmentCatalogAPI;
